import type {
  APIBookingDraftServiceCancellationInsurance,
  APIBookingDraft,
  APIBookingDraftServiceCancellationInsuranceOption,
} from '@/scopes/checkout/infrastructure/api/types';
import type { ServiceParticipantsMapper } from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper';
import { type CheckoutPerBookingService, type ServiceMapper, ServiceType } from '../../../domain/types';
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper';
import { createServiceParticipantsMapper } from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper';
import { isNil } from 'ramda';
import type { IconGlyph } from '@sunwebgroup/ui-components';

import { type AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types';

interface Dependencies {
  serviceName: string;
  serviceTypeMapper: string;
  serviceParticipantsMapper?: ServiceParticipantsMapper;
  serviceBannersMapper?: ServiceBannersMapper;
}

export const createCancellationInsuranceServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutPerBookingService> = (dependencies) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies;

  function getServiceName() {
    return serviceName;
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper;
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(
      ({ aName }) => aName === serviceName
    ) as APIBookingDraftServiceCancellationInsurance;

    if (!service) return null;

    const { aName, options } = service;
    const selectedValue = mapSelectedValue(service);
    return {
      id: aName,
      selectedValue,
      isSelected: !isNil(selectedValue),
      type: ServiceType.CancellationInsurance,
      features: mapFeatures(service),
      options: options
        .sort((a, b) => parseFloat(a.percentage ?? '') - parseFloat(b.percentage ?? ''))
        .map((option) => mapOption(option)),
      banners: serviceBannersMapper.map(service),
      participants: serviceParticipantsMapper.map(service, bookingDraft),
    };
  }

  function mapOption(option: APIBookingDraftServiceCancellationInsuranceOption) {
    const { bestValueText, code, percentage, modalInformation, features, text, percentageText } = option;

    return {
      label: text ?? '',
      value: code,
      percentage: !percentage?.includes('0,0') ? percentage : '',
      percentageText,
      price: 0,
      features,
      modalInformation,
      ...(bestValueText && { badge: bestValueText }),
    };
  }

  function mapFeatures(service: APIBookingDraftServiceCancellationInsurance) {
    if (!service.features) return null;
    const { features } = service;
    return features.map((feature) => {
      return {
        description: feature.description,
        code: feature.code,
        icon: mapFeatureIcon(feature.icon) ?? null,
        extraContent: feature.extraContent ?? {},
      };
    });
  }

  const mapFeatureIcon = (icon: string): IconGlyph | undefined => {
    const featureIconMap: Record<string, IconGlyph> = {
      money: 'IconMoney',
      'health-and-safety': 'IconHealthAndSafety',
      luggage: 'IconLuggage',
      '1-person-travelling': 'IconOnePersonTravelling',
    };

    return featureIconMap[icon];
  };

  function mapSelectedValue({ options }: APIBookingDraftServiceCancellationInsurance) {
    return options.find(({ isSelected }) => isSelected)?.code || null;
  }

  function mapSelection(service: CheckoutPerBookingService) {
    const { participants } = service;

    return Object.values(participants ?? {}).map(({ id, selectedValue }) => ({
      participantId: id,
      value: selectedValue,
    }));
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutPerBookingService,
    previousService: CheckoutPerBookingService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped = [];
    const ancillaryRemoveMapped = [];

    const previousOptionClicked = previousService?.options.find(({ value }) => value === previousService.selectedValue);
    const newOptionClicked = service?.options.find(({ value }) => value === service.selectedValue);

    if (newOptionClicked) {
      ancillaryAddMapped.push({
        id: newOptionClicked.value || '',
        name: newOptionClicked.label || '',
        price: newOptionClicked?.percentage || 0,
        category: `Ancillary > ${newOptionClicked?.badge ? 'Persuasion' : 'Additional cost'} > ${service.type}`,
        quantity: 1,
        serviceType: service.type,
        extraProperties: {},
      });
    }
    if (previousOptionClicked) {
      ancillaryRemoveMapped.push({
        id: previousOptionClicked.value || '',
        name: previousOptionClicked.label || '',
        price: previousOptionClicked?.percentage || 0,
        category: `Ancillary > ${previousOptionClicked?.badge ? 'Persuasion' : 'Additional cost'} > ${service.type}`,
        quantity: 1,
        serviceType: service.type,
        extraProperties: {},
      });
    }
    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  };
};
